/**
 * Admin Hub Breadcrumbs Component
 * Shows navigation path with clickable segments
 */
import React, { useMemo } from 'react';
import { Box, Breadcrumbs, Link, Typography, IconButton, Tooltip } from '@mui/material';
import {
  Home as HomeIcon,
  Search as SearchIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
} from '@mui/icons-material';
import { useAdminHub } from './context/AdminHubContext';
import { useAdminHubFavorites } from '../../hooks/useAdminHubFavorites';
import { getAllNavigablePages, type TreeNavItem } from '../../config/adminHubConfig';

interface AdminHubBreadcrumbsProps {
  showCommandPaletteButton?: boolean;
  showFavoriteButton?: boolean;
}

/**
 * Build breadcrumb path from current location
 */
const buildBreadcrumbPath = (
  activePath: string | null,
  navTree: TreeNavItem[]
): Array<{ label: string; path: string | null }> => {
  if (!activePath) return [];

  const path: Array<{ label: string; path: string | null }> = [];

  const findPath = (items: TreeNavItem[], currentPath: Array<{ label: string; path: string | null }>): boolean => {
    for (const item of items) {
      // Subheaders are visual grouping only — keep them out of the breadcrumb trail
      const newPath = item.type === 'subheader'
        ? currentPath
        : [...currentPath, { label: item.label, path: item.path || null }];

      // Base-path comparison so tab/query variations of a page still resolve
      if (item.path && item.path.split('?')[0] === activePath.split('?')[0]) {
        path.push(...newPath);
        return true;
      }

      if (item.children) {
        if (findPath(item.children, newPath)) {
          return true;
        }
      }
    }
    return false;
  };

  findPath(navTree, []);
  return path;
};

export const AdminHubBreadcrumbs: React.FC<AdminHubBreadcrumbsProps> = ({
  showCommandPaletteButton = true,
  showFavoriteButton = true,
}) => {
  const { activePath, navigateTo, openCommandPalette, filteredNavTree } = useAdminHub();
  const { isFavorite, toggleFavorite } = useAdminHubFavorites();

  const breadcrumbPath = useMemo(
    () => buildBreadcrumbPath(activePath, filteredNavTree),
    [activePath, filteredNavTree]
  );

  const currentPage = useMemo(() => {
    if (!activePath) return null;
    const pages = getAllNavigablePages(filteredNavTree);
    return pages.find(p => p.path === activePath)
      ?? pages.find(p => p.path.split('?')[0] === activePath.split('?')[0]);
  }, [activePath, filteredNavTree]);

  const currentIsFavorite = activePath ? isFavorite(activePath) : false;

  const handleFavoriteToggle = () => {
    if (currentPage && activePath) {
      toggleFavorite(activePath, currentPage.label, currentPage.section);
    }
  };

  // Show keyboard shortcut hint on hover
  const isMac = typeof navigator !== 'undefined' && navigator.platform.toUpperCase().indexOf('MAC') >= 0;
  const cmdKey = isMac ? '⌘' : 'Ctrl+';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 2,
        py: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
        backgroundColor: 'background.paper',
        minHeight: 48,
      }}
    >
      {/* Breadcrumb navigation */}
      <Breadcrumbs
        aria-label="Admin Hub navigation"
        sx={{
          '& .MuiBreadcrumbs-separator': {
            mx: 1,
          },
          '& .MuiBreadcrumbs-li': {
            display: 'flex',
            alignItems: 'center',
          },
        }}
      >
        {/* Home/Dashboard link */}
        <Link
          component="button"
          underline="hover"
          onClick={() => navigateTo('/admin/hub')}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            color: 'text.secondary',
            cursor: 'pointer',
            fontSize: '0.8125rem',
            '&:hover': {
              color: 'primary.main',
            },
          }}
        >
          <HomeIcon sx={{ fontSize: 18 }} />
          Admin Hub
        </Link>

        {/* Path segments */}
        {breadcrumbPath.map((segment, index) => {
          const isLast = index === breadcrumbPath.length - 1;

          if (isLast) {
            return (
              <Typography
                key={segment.label}
                color="text.primary"
                sx={{
                  fontWeight: 500,
                  fontSize: '0.8125rem',
                }}
              >
                {segment.label}
              </Typography>
            );
          }

          return (
            <Link
              key={segment.label}
              component="button"
              underline="hover"
              onClick={() => segment.path && navigateTo(segment.path)}
              sx={{
                color: segment.path ? 'text.secondary' : 'text.disabled',
                cursor: segment.path ? 'pointer' : 'default',
                fontSize: '0.8125rem',
                '&:hover': {
                  color: segment.path ? 'primary.main' : 'text.disabled',
                },
              }}
            >
              {segment.label}
            </Link>
          );
        })}
      </Breadcrumbs>

      {/* Action buttons */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {/* Favorite button */}
        {showFavoriteButton && currentPage && (
          <Tooltip title={currentIsFavorite ? 'Remove from favorites' : 'Add to favorites'}>
            <IconButton
              size="small"
              onClick={handleFavoriteToggle}
              sx={{
                color: currentIsFavorite ? 'warning.main' : 'text.secondary',
              }}
            >
              {currentIsFavorite ? <StarIcon /> : <StarBorderIcon />}
            </IconButton>
          </Tooltip>
        )}

        {/* Command palette button */}
        {showCommandPaletteButton && (
          <Tooltip title={`Search (${cmdKey}K)`}>
            <IconButton
              size="small"
              onClick={openCommandPalette}
              sx={{
                color: 'text.secondary',
              }}
            >
              <SearchIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

export default AdminHubBreadcrumbs;
